import React, {useState} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {applyWebUsersFilter} from '../../actions/users';
import {
  rowSurfaces,
  WEB_USER_PRESENCE_LABELS,
  WEB_USER_PRESENCES,
  WebUserPresence,
  WebUserRow,
  WebUsersFilter,
} from '../../models/WebUser';
import {toCsv} from '../../utils/CsvUtils';

interface Props {
    filter: WebUsersFilter;
    users: WebUserRow[];
    isLoading: boolean;
    hasMore: boolean;
    applyWebUsersFilter: (filter: WebUsersFilter) => void;
}

/** `<input type="date">` gives YYYY-MM-DD; the backend compares it as an inclusive lower bound. */
const endOfDay = (date: string | null) => (date ? `${date} 23:59:59.999` : null);

const UsersControlPanel = ({filter, users, isLoading, hasMore, applyWebUsersFilter}: Props) => {
  const [draft, setDraft] = useState<WebUsersFilter>(filter);

  const set = (key: keyof WebUsersFilter) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraft({...draft, [key]: event.target.value || null});
  };

  // Presence is a set of buckets: none selected means no filtering at all, which is why the
  // cleared state is null rather than an empty array the backend would have to interpret.
  const togglePresence = (presence: WebUserPresence) => () => {
    const selected = draft.presence ?? [];
    const next = selected.includes(presence)
      ? selected.filter((item) => item !== presence)
      : [...selected, presence];
    setDraft({...draft, presence: next.length > 0 ? next : null});
  };

  const apply = () => applyWebUsersFilter({
    createdFrom: draft.createdFrom,
    createdTo: endOfDay(draft.createdTo),
    statusChangedFrom: draft.statusChangedFrom,
    statusChangedTo: endOfDay(draft.statusChangedTo),
    presence: draft.presence,
  });

  const reset = () => {
    const empty = {
      createdFrom: null, createdTo: null, statusChangedFrom: null, statusChangedTo: null, presence: null,
    };
    setDraft(empty);
    applyWebUsersFilter(empty);
  };

  // Exported from what is already loaded, so the file matches what the table shows. The button
  // stays disabled while pages are still arriving — otherwise the CSV would silently be partial.
  const exportCsv = () => {
    const csv = toCsv(
        ['email', 'created_at', 'purchase_status', 'subscription_status', 'source',
          'used_in', 'presence', 'language', 'status_changed_at'],
        users.map((user) => [
          user.email ?? '',
          user.createdAt,
          user.hasPurchase ? 'bought' : 'not bought',
          user.subscriptionStatus,
          user.source ?? '',
          rowSurfaces(user).join(' '),
          user.presence ?? '',
          user.language ?? '',
          user.statusChangedAt ?? '',
        ]),
    );
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8'}));
    link.download = `web-users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    // Two groups instead of one flat flex row: the filters may wrap among themselves on a narrow
    // window, while the actions block keeps its own line and stays pinned right. Flat wrapping was
    // what threw the Export button onto a second row (so it appeared on the left) and back again
    // whenever the summary changed width.
    <div className={'users-panel'}>
      <div className={'users-panel-filters'}>
        <div className={'users-filter-group'}>
          <span className={'users-filter-label'}>Created</span>
          <input type="date" value={draft.createdFrom?.slice(0, 10) ?? ''} onChange={set('createdFrom')}/>
          <span className={'users-filter-dash'}>—</span>
          <input type="date" value={draft.createdTo?.slice(0, 10) ?? ''} onChange={set('createdTo')}/>
        </div>

        <div className={'users-filter-group'}>
          <span className={'users-filter-label'}>Status changed</span>
          <input type="date" value={draft.statusChangedFrom?.slice(0, 10) ?? ''} onChange={set('statusChangedFrom')}/>
          <span className={'users-filter-dash'}>—</span>
          <input type="date" value={draft.statusChangedTo?.slice(0, 10) ?? ''} onChange={set('statusChangedTo')}/>
        </div>

        {/* Presence buckets: funnel only / funnel and app / app only, plus the rows that predate
            surface tracking. Filtered on the server so pagination stays correct. */}
        <div className={'users-filter-group'}>
          <span className={'users-filter-label'}>Used in</span>
          {WEB_USER_PRESENCES.map((presence) => (
            <label key={presence} className={'users-filter-check'}>
              <input
                type="checkbox"
                checked={(draft.presence ?? []).includes(presence)}
                onChange={togglePresence(presence)}
              />
              {WEB_USER_PRESENCE_LABELS[presence]}
            </label>
          ))}
        </div>

        <button className={'users-panel-button'} onClick={apply} disabled={isLoading}>Apply</button>
        <button className={'users-panel-button'} onClick={reset} disabled={isLoading}>Reset</button>
      </div>

      <div className={'users-panel-actions'}>
        {/* Each counter sits in its own fixed-width slot with tabular figures, so a page arriving
            and bumping the numbers cannot shift the words next to it — or the button after it. */}
        <div className={'users-summary'}>
          <span className={'users-summary-value'}>{users.length}</span>
          <span>loaded</span>
          <span className={'users-summary-value'}>{users.filter((user) => user.hasPurchase).length}</span>
          <span>bought</span>
        </div>

        {/* Fixed width so the disabled/enabled switch cannot resize it either — the label never
            changes, only the tooltip does. */}
        <button
          className={'users-panel-button users-export-button'}
          onClick={exportCsv}
          disabled={isLoading || hasMore || users.length === 0}
          title={hasMore ? 'Wait until every page is loaded' : 'Export the loaded list'}
        >
          Export CSV
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  filter: state.users.filter,
  users: state.users.users,
  isLoading: state.users.isLoading,
  hasMore: state.users.hasMore,
});

const mapDispatchToProps = (dispatch: any) => ({
  applyWebUsersFilter: bindActionCreators(applyWebUsersFilter, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersControlPanel);
